import React, { useEffect, useState } from 'react';
import { Card, Table, Space, Button, Select, Tag, Segmented, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, FilterOutlined, CrownOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { AppSettings, GodWarRankingItem, SelectOption, HistoryGranularity } from '../types';
import { godWarAPI, configAPI, historyAPI } from '../services/api';
import { compareSnapshotsDesc } from '../utils/snapshot';
import HistoryControls from './HistoryControls';

interface Props { settings: AppSettings }

const GodWarRankings: React.FC<Props> = ({ settings }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<GodWarRankingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState<'plunder' | 'devil'>('plunder');
  const [characters, setCharacters] = useState<SelectOption[]>([]);
  const [characterId, setCharacterId] = useState<number | null>(null);
  // 历史对比
  const [historyEnabled, setHistoryEnabled] = useState(false);
  const [granularity, setGranularity] = useState<HistoryGranularity>('day');
  const [snapshots, setSnapshots] = useState<string[]>([]);
  const [snapshotFrom, setSnapshotFrom] = useState<string | undefined>(undefined);
  const [snapshotTo, setSnapshotTo] = useState<string | undefined>(undefined);

  useEffect(() => {
    configAPI.getCharacters().then(setCharacters).catch(() => {});
  }, []);

  const load = async (p = 1) => {
    setLoading(true);
    try {
      if (historyEnabled && snapshotFrom && snapshotTo) {
        const category = type === 'plunder' ? 'godwar_plunder' : 'godwar_devil' as const;
        const res = await historyAPI.compare<GodWarRankingItem>({
          category,
          granularity,
          snapshot_from: snapshotFrom,
          snapshot_to: snapshotTo,
          server_id: settings.serverId ?? undefined,
          page: p,
          page_size: settings.pageSize,
        });
  const items = (res.diffs || []).map((d: any, index: number) => {
          const toItem = d.to || {};
          const fromItem = d.from || {};
          const newRank = (d.new_rank ?? toItem.rank ?? toItem.global_rank ?? 0) as number;
          const oldRank = (d.old_rank ?? fromItem.rank ?? fromItem.global_rank ?? 0) as number;
          // 分数优先用 score（神魔的“分数”），战力用 fightpoint；差值严格同字段相减
          const toScore = toItem.score != null ? toItem.score : undefined;
          const fromScore = fromItem.score != null ? fromItem.score : undefined;
          let score_delta = 0;
          if (toScore != null && fromScore != null) score_delta = toScore - fromScore;
          else if (toScore != null) score_delta = toScore;
          else if (fromScore != null) score_delta = -fromScore;
          const rank_delta = newRank - oldRank;
          const formatted_fightpoint = toItem.formatted_fightpoint ?? (toItem.fightpoint != null ? String(toItem.fightpoint) : undefined);
          const formatted_score = toItem.formatted_score ?? (toItem.score != null ? String(toItem.score) : undefined);
          return { ...toItem, rank: newRank, score_delta, rank_delta, formatted_fightpoint, formatted_score, is_new: !!d.is_new } as GodWarRankingItem & { score_delta: number; rank_delta: number; is_new?: boolean };
        }).filter(i => !characterId || i.character_id === characterId);
        setData(items);
        setTotal(res.total ?? items.length);
        setPage(res.page ?? p);
      } else if (snapshotTo) {
        const category = type === 'plunder' ? 'godwar_plunder' : 'godwar_devil' as const;
        const res = await historyAPI.getHistory<GodWarRankingItem>({
          category,
          granularity,
          snapshot: snapshotTo,
          server_id: settings.serverId ?? undefined,
          page: p,
          page_size: settings.pageSize,
        });
        const items = (res.items || []).map((it: any) => ({
          ...it,
          rank: it.rank ?? it.global_rank ?? 0,
          formatted_fightpoint: it.formatted_fightpoint ?? (it.fightpoint != null ? String(it.fightpoint) : undefined),
          formatted_score: it.formatted_score ?? (it.score != null ? String(it.score) : undefined),
        })).filter(i => !characterId || i.character_id === characterId);
        setData(items);
        setTotal(res.total ?? items.length);
        setPage(res.page ?? p);
      } else {
        const res = await godWarAPI.getRankings({
          page: p,
          page_size: settings.pageSize,
          type,
          server_id: settings.serverId,
          use_units: settings.useUnits,
        });
        const items = res.items.filter(i => !characterId || i.character_id === characterId);
        setData(items);
        setTotal(res.total);
        setPage(p);
      }
    } catch (e) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(1); }, [settings.pageSize, settings.serverId, settings.useUnits, type, characterId, historyEnabled, granularity, snapshotFrom, snapshotTo]);

  useEffect(() => {
    const fetchSnapshots = async () => {
      try {
        const category = type === 'plunder' ? 'godwar_plunder' : 'godwar_devil' as const;
        const resp = await historyAPI.listSnapshots({ category, granularity, limit: 60 });
  const snaps = (resp.snapshots || []).slice().sort(compareSnapshotsDesc);
        setSnapshots(snaps);
        if (historyEnabled) {
          if (snaps.length >= 2) {
            setSnapshotFrom(snaps[1]);
            setSnapshotTo(snaps[0]);
          }
        } else {
          if (snaps.length >= 1) {
            setSnapshotTo(snaps[0]);
          }
        }
      } catch {}
    };
    fetchSnapshots();
  }, [historyEnabled, granularity, settings.serverId, type]);

  const columns: ColumnsType<any> = [
    { title: '全服排名', dataIndex: 'rank', key: 'rank', width: 80, align: 'center', render: (v: number) => <Tag color={v<=3?'gold':'blue'} style={{ fontWeight: 'bold' }}>{v}</Tag> },
    { title: '玩家', dataIndex: 'name', key: 'name', width: 140, align: 'center', render: name => <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{name}</span> },
    { title: '战力', dataIndex: 'formatted_fightpoint', key: 'fightpoint', width: 120, align: 'center', render: fp => <span style={{ fontWeight: 'bold', color: '#fa8c16' }}>{fp}</span> },
    { title: '分数', dataIndex: 'formatted_score', key: 'score', width: 120, align: 'center', render: score => <span style={{ fontWeight: 'bold', color: '#f5222d' }}>{score}</span> },
    { title: 'UID', dataIndex: 'uid', key: 'uid', width: 140, align: 'center', render: uid => <span style={{ color: '#722ed1' }}>{uid}</span> },
    { title: '角色', dataIndex: 'character_name', key: 'character_name', width: 120, align: 'center', render: name => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{name}</span> },
    { title: '联盟', dataIndex: 'league_name', key: 'league_name', width: 140, align: 'center', render: (t: string | undefined) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{t||'无'}</span> },
    { title: '地区', dataIndex: 'ip_area', key: 'ip_area', width: 120, align: 'center', render: ip => <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>{ip}</span> },
    // 对比模式附加列
    ...(historyEnabled ? [
      { title: '分数变化', dataIndex: 'score_delta', key: 'score_delta', width: 110, align: 'center' as const, render: (delta: number) => (
        <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) > 0 ? '#52c41a' : '#f5222d', fontWeight: 500 }}>
          {delta > 0 ? `+${delta}` : `${delta || 0}`}
        </span>
      ) },
      { title: '排名变化', dataIndex: 'rank_delta', key: 'rank_delta', width: 120, align: 'center' as const, render: (delta: number, record: any) => {
        if (record?.is_new) {
          return <Tag color="green" style={{ fontWeight: 700 }}>新上榜</Tag>;
        }
        return (
          <span style={{ color: (delta ?? 0) === 0 ? '#666' : (delta ?? 0) < 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}>
            {delta < 0 ? `⬆ ${Math.abs(delta)}` : delta > 0 ? `⬇︎ ${delta}` : '—'}
          </span>
        );
      } },
    ] : []),
  ];

  return (
    <Card 
      title={<Space>{type==='plunder'?<CrownOutlined/>:<ThunderboltOutlined/>}{type==='plunder'?'神将排行榜':'魔王排行榜'}</Space>}
      extra={
        <Space>
          <HistoryControls
            enabled={historyEnabled}
            granularity={granularity}
            snapshots={snapshots}
            from={snapshotFrom}
            to={snapshotTo}
            onChange={(v) => { setHistoryEnabled(v.enabled); setGranularity(v.granularity); setSnapshotFrom(v.from); setSnapshotTo(v.to); }}
          />
          <Segmented 
            value={type}
            onChange={(val)=>setType(val as any)}
            options={[{label:'神将',value:'plunder'},{label:'魔王',value:'devil'}]}
          />
          <FilterOutlined />
          <Select
            placeholder="选择角色"
            style={{ width: 140 }}
            value={characterId}
            onChange={(v)=>setCharacterId(v)}
            allowClear
          >
            {characters.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={()=>load(page)} loading={loading}>
            刷新
          </Button>
        </Space>
      }
    >
  <Table<GodWarRankingItem>
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={r=>`${r.uid}-${r.server_id}`}
        pagination={{
          current: page, pageSize: settings.pageSize, total, showTotal: t=>`共 ${t} 条`, onChange: p=>load(p)
        }}
        scroll={{ x: 900 }}
        size="small"
        bordered
      />
    </Card>
  );
};

export default GodWarRankings;
